import React, { useCallback, useEffect, useState } from 'react';
import { apiPost, isBackendConfigured } from '../utils/backendApi';
import { OrgAgree } from './Clickwrap';
import { legalStatus } from '../data/legal';

// The Marketing add-on card on Settings: what it is, Add it (Stripe checkout
// behind the current clickwrap — the same Terms/Privacy/EULA set, re-accepted
// when a new version is posted), and once on, "Open Marketing" — a 90-second
// SSO ticket that lands the member in the organization's own org on the
// shared Marketing instance with the role their seat here maps to.
export default function MarketingAddon({ org, myRole }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [payDocs, setPayDocs] = useState(null);
  const isAdmin = ['admin', 'owner'].includes(myRole);

  const load = useCallback(async () => {
    if (!isBackendConfigured() || !org?.id) return;
    try { setInfo(await apiPost('/billing/marketing/info', { orgId: org.id })); setErr(''); } catch (e) { setErr(e.message); }
  }, [org?.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label); setErr('');
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const checkout = () => act('buy', async () => {
    const out = await apiPost('/billing/checkout', { orgId: org.id, addonKey: 'marketing' });
    if (out?.url) window.location.assign(out.url); else await load();
  });
  const buy = () => act('buy', async () => {
    const status = await legalStatus(org.id);
    if (!status.orgOk) { setPayDocs(status.agreements || []); return; }
    await checkout();
  });
  const open = () => act('open', async () => {
    const out = await apiPost('/billing/marketing/sso', { orgId: org.id, next: '/' });
    if (out?.url) window.location.assign(out.url);
  });

  if (!isBackendConfigured() || !info) return null;
  const a = info.addon || {};
  return (
    <section
      style={{ marginTop: 28 }}
      data-nova-note={`Marketing add-on (${a.priceLabel || 'priced on the pricing page'}): ${info.entitled ? 'active — Open Marketing signs the person into the organization\u2019s own org on the shared Marketing instance' : 'not active — an organization admin can add it; checkout runs behind the current agreements'}. Buying is the person\u2019s click.`}
    >
      <h2 style={{ margin: 0 }}>{a.label || 'Marketing'} <span className="mf-muted" style={{ fontWeight: 400, fontSize: 16 }}>· add-on{a.priceLabel ? ` · ${a.priceLabel}` : ''}</span></h2>
      <p className="cs-help" style={{ margin: '6px 0 0', maxWidth: 640 }}>{a.blurb}</p>
      {err && <p className="cs-form-error" role="alert" style={{ marginTop: 8 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        {info.entitled ? (
          <>
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'open'} onClick={open} data-agent-guard="signs you into another product">
              {busy === 'open' ? 'Opening…' : 'Open Marketing'}
            </button>
            <span className="mf-muted" style={{ fontSize: 12 }}>
              Same account, same agreements — you arrive as {['admin', 'owner'].includes(info.role) ? 'an admin' : info.role === 'member' ? 'a member' : 'a viewer'} of your organization there.
              {info.instance?.seats != null ? ` ${info.instance.seats} seat${info.instance.seats === 1 ? '' : 's'} carried over.` : ''}
            </span>
          </>
        ) : isAdmin ? (
          <>
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'buy' || !a.configured} onClick={buy} data-agent-noclick title={a.configured ? '' : 'Not available yet'}>
              {busy === 'buy' ? 'Starting…' : `Add ${a.label || 'Marketing'}`}
            </button>
            <span className="mf-muted" style={{ fontSize: 12 }}>Covered by the Terms, Privacy Policy and End User Agreement you already accepted; no new agreement set. Hosted model usage is metered to this organization.</span>
          </>
        ) : <span className="cs-help">An organization admin can add it.</span>}
      </div>
      {payDocs && <OrgAgree orgId={org.id} docs={payDocs} onDone={() => { setPayDocs(null); checkout(); }} />}
    </section>
  );
}
